import { useCallback, useEffect, useMemo, useState } from "react"
import api from "../../utils/api"
import { getApiErrorMessage } from "../../utils/errorMessage"
import { getUtilityDebtSummary, getUtilityWalletBalance } from "./utilitySettlement"

// Centralizes all UT Bills data loading and mutations so the screens stay
// presentational. Mutations resolve/throw so callers (modals) can show inline
// feedback; reads update shared state and refresh the relevant slices.
export function useUTBills() {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState("")
  const [wallet, setWallet] = useState(null)
  const [accountHolder, setAccountHolder] = useState(null)
  const [topUpEnabled, setTopUpEnabled] = useState(false)
  const [invoices, setInvoices] = useState([])
  const [utilityStatement, setUtilityStatement] = useState(null)
  const [statementRows, setStatementRows] = useState([])
  const [statementSummary, setStatementSummary] = useState(null)
  const [providers, setProviders] = useState([])
  const [selectedProviderId, setSelectedProviderId] = useState("")
  const [accounts, setAccounts] = useState([])
  const [categories, setCategories] = useState([])
  const [providerInvoices, setProviderInvoices] = useState([])
  const [providerStatement, setProviderStatement] = useState(null)
  const [providerCustomers, setProviderCustomers] = useState([])
  const [providerCustomersLoading, setProviderCustomersLoading] = useState(false)
  const [prepaymentCredit, setPrepaymentCredit] = useState({ issuers: [], totalRemaining: 0 })
  // Companies that bill this person and whether each may be paid by itself.
  const [autopayCompanies, setAutopayCompanies] = useState({ walletBalance: 0, companies: [] })

  const outstandingTotal = useMemo(
    () => getUtilityDebtSummary(statementSummary, invoices).amountDue,
    [statementSummary, invoices],
  )

  const selectedProvider = useMemo(
    () => providers.find((provider) => String(provider._id) === String(selectedProviderId)) || null,
    [providers, selectedProviderId]
  )

  const loadCustomerSide = useCallback(async () => {
    const [walletRes, invoiceRes, statementRes, prepaymentRes, autopayRes] = await Promise.all([
      api.get("/ut-bills/my-wallet"),
      api.get("/ut-bills/my-invoices"),
      api.get("/utilities/my-statement"),
      api.get("/utilities/prepayments"),
      api.get("/ut-bills/autopay-approvals"),
    ])
    setAutopayCompanies({
      walletBalance: Number(autopayRes?.data?.walletBalance || 0),
      companies: Array.isArray(autopayRes?.data?.companies) ? autopayRes.data.companies : [],
    })
    setPrepaymentCredit({
      issuers: Array.isArray(prepaymentRes?.data?.issuers) ? prepaymentRes.data.issuers : [],
      totalRemaining: Number(prepaymentRes?.data?.totalRemaining || 0),
    })
    const walletData = walletRes?.data?.wallet || (walletRes?.data?.walletBalance != null ? {} : null)
    setWallet(walletData ? {
      ...walletData,
      walletBalance: getUtilityWalletBalance(walletRes?.data || walletData),
    } : null)
    setAccountHolder(walletRes?.data?.accountHolder || null)
    setTopUpEnabled(walletRes?.data?.topUpEnabled === true)
    setInvoices(Array.isArray(invoiceRes?.data?.invoices) ? invoiceRes.data.invoices : [])
    const statement = statementRes?.data?.statement || {}
    setUtilityStatement(statement)
    setStatementRows(Array.isArray(statement.rows) ? statement.rows : [])
    setStatementSummary(statement.summary || null)
  }, [])

  const loadProviders = useCallback(async () => {
    const response = await api.get("/ut-bills/providers")
    const next = Array.isArray(response?.data?.providers) ? response.data.providers : []
    setProviders(next)
    setSelectedProviderId((current) => {
      if (current && next.some((provider) => String(provider._id) === String(current))) return current
      return next[0]?._id || ""
    })
    return next
  }, [])

  const loadProviderStatement = useCallback(async () => {
    const response = await api.get("/ut-bills/provider-statement")
    setProviderStatement(response?.data || null)
  }, [])

  const loadAccounts = useCallback(async (providerId) => {
    if (!providerId) {
      setAccounts([])
      setCategories([])
      return
    }
    const response = await api.get(`/ut-bills/providers/${encodeURIComponent(providerId)}/accounts`)
    setAccounts(Array.isArray(response?.data?.accounts) ? response.data.accounts : [])
    setCategories(Array.isArray(response?.data?.categories) ? response.data.categories : [])
  }, [])

  const loadProviderInvoices = useCallback(async (providerId) => {
    if (!providerId) {
      setProviderInvoices([])
      return
    }
    const response = await api.get(`/ut-bills/providers/${encodeURIComponent(providerId)}/invoices`)
    setProviderInvoices(Array.isArray(response?.data?.invoices) ? response.data.invoices : [])
  }, [])

  // Who this company bills and how much each person still owes. Kept beside the
  // provider's own invoice list so both refresh together after every mutation.
  const loadProviderCustomers = useCallback(async (providerId) => {
    if (!providerId) {
      setProviderCustomers([])
      setProviderCustomersLoading(false)
      return
    }
    setProviderCustomersLoading(true)
    try {
      const response = await api.get(
        `/ut-bills/providers/${encodeURIComponent(providerId)}/customer-balances`,
      )
      setProviderCustomers(Array.isArray(response?.data?.customers) ? response.data.customers : [])
    } finally {
      setProviderCustomersLoading(false)
    }
  }, [])

  const fetchCustomerStatement = useCallback(async (providerId, customerId) => {
    const response = await api.get(
      `/ut-bills/providers/${encodeURIComponent(providerId)}/customers/${encodeURIComponent(customerId)}/statement`,
    )
    return response?.data || null
  }, [])

  const refreshAll = useCallback(async () => {
    setError("")
    await Promise.all([loadCustomerSide(), loadProviders(), loadProviderStatement()])
  }, [loadCustomerSide, loadProviders, loadProviderStatement])

  // Everything a billing mutation can change: the payer's own view, the money
  // totals, the company's setup, its bills, and its customer balances.
  const refreshProviderData = useCallback((providerId) => Promise.all([
    loadCustomerSide(),
    loadProviderStatement(),
    loadAccounts(providerId),
    loadProviderInvoices(providerId),
    loadProviderCustomers(providerId),
  ]), [
    loadCustomerSide,
    loadProviderStatement,
    loadAccounts,
    loadProviderInvoices,
    loadProviderCustomers,
  ])

  useEffect(() => {
    let active = true
    const run = async () => {
      try {
        setLoading(true)
        await refreshAll()
      } catch (err) {
        if (active) setError(getApiErrorMessage(err, "Failed to load Bills & Invoices."))
      } finally {
        if (active) setLoading(false)
      }
    }
    run()
    return () => {
      active = false
    }
  }, [refreshAll])

  useEffect(() => {
    Promise.all([
      loadAccounts(selectedProviderId),
      loadProviderInvoices(selectedProviderId),
      loadProviderCustomers(selectedProviderId),
    ]).catch((err) => {
      setAccounts([])
      setProviderInvoices([])
      setProviderCustomers([])
      setError(getApiErrorMessage(err, "Failed to load provider billing data."))
    })
  }, [selectedProviderId, loadAccounts, loadProviderInvoices, loadProviderCustomers])

  const createProvider = useCallback(async (payload) => {
    await api.post("/ut-bills/providers", payload)
    await refreshAll()
  }, [refreshAll])

  const createCategory = useCallback(async (providerId, payload) => {
    const response = await api.post(`/ut-bills/providers/${encodeURIComponent(providerId)}/categories`, payload)
    await loadAccounts(providerId)
    return response?.data?.category || null
  }, [loadAccounts])

  const configureCategoryAutoInvoice = useCallback(async (providerId, categoryId, payload) => {
    const response = await api.patch(
      `/ut-bills/providers/${encodeURIComponent(providerId)}/categories/${encodeURIComponent(categoryId)}/auto-invoice`,
      payload
    )
    if (payload?.enabled === false) {
      await refreshProviderData(providerId)
    } else {
      // The bills are created by a durable background job. Refresh the visible
      // setting without keeping the success confirmation waiting on the reads.
      refreshProviderData(providerId).catch((err) => {
        setError(getApiErrorMessage(err, "Auto-invoicing was saved, but the page could not refresh."))
      })
    }
    return response?.data || null
  }, [refreshProviderData])

  // Bill creation returns the response so the console can say whether the
  // customer still has to approve automatic payment.
  const createInvoice = useCallback(async (providerId, payload) => {
    const response = await api.post(`/ut-bills/providers/${encodeURIComponent(providerId)}/invoices`, payload)
    await refreshProviderData(providerId)
    return response?.data || null
  }, [refreshProviderData])

  // One-step bill issuance: resolves/registers the user, finds/creates the
  // (customer + category) account, and issues the bill in a single call.
  const createBill = useCallback(async (providerId, payload) => {
    const response = await api.post(`/ut-bills/providers/${encodeURIComponent(providerId)}/bills`, payload)
    await refreshProviderData(providerId)
    return response?.data || null
  }, [refreshProviderData])

  // The billing company asks a customer to allow automatic payment.
  const requestAutopayApproval = useCallback(async (providerId, customerId) => {
    const response = await api.post(
      `/ut-bills/providers/${encodeURIComponent(providerId)}/customers/${encodeURIComponent(customerId)}/autopay-request`,
    )
    await loadProviderCustomers(providerId)
    return response?.data || null
  }, [loadProviderCustomers])

  // The customer turns automatic payment on or off for one company. Turning it
  // on can pay waiting bills, so the whole payer view reloads.
  const setAutopayApproval = useCallback(async (providerId, approved) => {
    const response = await api.put(`/ut-bills/autopay-approvals/${encodeURIComponent(providerId)}`, { approved })
    await loadCustomerSide()
    return response?.data || null
  }, [loadCustomerSide])

  // The customer pays one bill from the wallet on purpose: all of it, or only the
  // amount they chose.
  const payBillFromWallet = useCallback(async (invoiceId, idempotencyKey, amount = null) => {
    const response = await api.post(
      `/ut-bills/autopay-approvals/bills/${encodeURIComponent(invoiceId)}/pay`,
      amount === null || amount === undefined ? { idempotencyKey } : { idempotencyKey, amount },
    )
    await loadCustomerSide()
    return response?.data || null
  }, [loadCustomerSide])

  // The customer disputes a bill amount. The bill waits until the company enters
  // the amount again.
  const objectToBill = useCallback(async (invoiceId, message) => {
    const response = await api.post(`/ut-bills/objections/bills/${encodeURIComponent(invoiceId)}`, { message })
    await loadCustomerSide()
    return response?.data || null
  }, [loadCustomerSide])

  const waiveInvoice = useCallback(async (providerId, invoiceId, payload) => {
    await api.post(`/ut-bills/providers/${encodeURIComponent(providerId)}/invoices/${encodeURIComponent(invoiceId)}/waive`, payload)
    await refreshProviderData(providerId)
  }, [refreshProviderData])

  const editInvoice = useCallback(async (providerId, invoiceId, payload) => {
    await api.put(`/ut-bills/providers/${encodeURIComponent(providerId)}/invoices/${encodeURIComponent(invoiceId)}`, payload)
    await refreshProviderData(providerId)
  }, [refreshProviderData])

  const deleteInvoice = useCallback(async (providerId, invoiceId) => {
    await api.delete(`/ut-bills/providers/${encodeURIComponent(providerId)}/invoices/${encodeURIComponent(invoiceId)}`)
    await refreshProviderData(providerId)
  }, [refreshProviderData])

  const createExpense = useCallback(async (providerId, payload) => {
    const response = await api.post(`/ut-bills/providers/${encodeURIComponent(providerId)}/expenses`, payload)
    await loadProviderStatement()
    return response?.data?.expense || null
  }, [loadProviderStatement])

  const addFunds = useCallback(async (amount) => {
    await api.post("/ut-bills/add-funds", { amount: Number(amount) })
    await loadCustomerSide()
  }, [loadCustomerSide])

  // With a provider, the biller can also find its own customers by name or AKA.
  const searchCustomers = useCallback(async (query, providerId = "") => {
    const q = String(query || "").trim()
    if (q.length < 2) return []
    const providerParam = providerId ? `&providerId=${encodeURIComponent(providerId)}` : ""
    const response = await api.get(`/ut-bills/customers/search?q=${encodeURIComponent(q)}${providerParam}`)
    return Array.isArray(response?.data?.users) ? response.data.users : []
  }, [])

  return {
    loading,
    error,
    setError,
    wallet,
    accountHolder,
    topUpEnabled,
    invoices,
    outstandingTotal,
    utilityStatement,
    statementRows,
    statementSummary,
    providers,
    selectedProviderId,
    setSelectedProviderId,
    selectedProvider,
    accounts,
    categories,
    providerInvoices,
    providerStatement,
    providerCustomers,
    providerCustomersLoading,
    fetchCustomerStatement,
    refreshAll,
    createProvider,
    createCategory,
    configureCategoryAutoInvoice,
    createInvoice,
    createBill,
    waiveInvoice,
    editInvoice,
    deleteInvoice,
    createExpense,
    addFunds,
    prepaymentCredit,
    // A prepayment moves wallet money and can clear open bills, so the whole
    // payer view is reloaded after one.
    reloadCustomerSide: loadCustomerSide,
    searchCustomers,
    autopayCompanies,
    setAutopayApproval,
    payBillFromWallet,
    objectToBill,
    requestAutopayApproval,
  }
}
